import { createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit'
import { orderBy, isEqual, cloneDeep } from 'lodash'
import { API, graphqlOperation } from 'aws-amplify'
import * as queries from '../../graphql/queries'
import * as mutations from '../../graphql/mutations'
import { makeErrorToast, msg, makeSuccessToast } from '../../utils/makeToast'
import {
  fetchProposals,
  setCurrentProposal,
  setCurrentProposalInvoices,
  setCurrentDraft,
} from './proposals'
import { fetchContacts } from './contacts'
import { fetchInvoicesByTenantIdUtil } from '@src/utils/fetchUtils'
import i18next from 'i18next'
import dayjs from 'dayjs'

export const fetchInvoices = createAsyncThunk(
  'invoices/fetchInvoices',
  async (arg, { getState, rejectWithValue }) => {
    const { auth, settings } = getState()
    try {
      const sort = settings.sort
      // const {
      //   data: {
      //     invoicesByTenantId: { items },
      //   },
      // } = await API.graphql(
      //   graphqlOperation(queries.invoicesByTenantId, {
      //     tenantId: auth.userInfo.tenantId,
      //     filter: { activeFlag: { eq: true } },
      //   })
      // )
      const invoicesData = await fetchInvoicesByTenantIdUtil(auth.userInfo.tenantId)

      return invoicesData
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)
export const createCreditInvoice = createAsyncThunk(
  'invoices/createCreditInvoice',
  async ({ creditInvoiceData, originalInvoiceData }, { getState, dispatch, rejectWithValue }) => {
    const { auth, settings } = getState()
    try {
      delete creditInvoiceData.invoiceTotal
      console.log('originalInvoiceData: ', originalInvoiceData)
      console.log('creditInvoiceData: ', creditInvoiceData)
      let copyInvoiceData = cloneDeep(originalInvoiceData)
      // delete originalInvoiceData.id
      delete copyInvoiceData.tenantId
      // delete copyInvoiceData.proposalId
      delete copyInvoiceData.activeFlag
      delete copyInvoiceData.archivedDate
      delete copyInvoiceData.paidDate
      delete copyInvoiceData.title
      delete copyInvoiceData.invoiceNumber
      delete copyInvoiceData.poNumber
      delete copyInvoiceData.invoiceAmountPercentage
      delete copyInvoiceData.amount
      delete copyInvoiceData.amountAfterTax
      delete copyInvoiceData.taxAmount
      delete copyInvoiceData.balancePercentage
      delete copyInvoiceData.balanceAmount
      delete copyInvoiceData.invoiceDate
      delete copyInvoiceData.description
      delete copyInvoiceData.proposalsDraft
      delete copyInvoiceData.proposals
      delete copyInvoiceData.createdOn
      delete copyInvoiceData.updatedOn
      delete copyInvoiceData._version
      delete copyInvoiceData._deleted
      delete copyInvoiceData._lastChangedAt

      const zeroPad = (num, places) => String(num).padStart(places, '0')

      const {
        data: { getCompany: companyDetails },
      } = await API.graphql(graphqlOperation(queries.getCompany, { id: auth.userInfo.tenantId }))
      let newInvoiceNumber = 0
      let newInvoicingyear = ''
      if (companyDetails.invoiceCount === null) {
        newInvoiceNumber = zeroPad(1, 4)
      } else {
        newInvoiceNumber = zeroPad(parseInt(companyDetails.invoiceCount) + 1, 4)
        newInvoicingyear = dayjs().toISOString()
      }
      if (companyDetails.invoicingYear === null) {
        newInvoicingyear = dayjs().toISOString()
      } else {
        if (dayjs().diff(dayjs(companyDetails.invoicingYear), 'year') > 0) {
          newInvoicingyear = dayjs().toISOString()
          newInvoiceNumber = zeroPad(1, 4)
        }
      }
      let year = dayjs(newInvoicingyear).year()

      const {
        data: { getContact },
      } = await API.graphql(
        graphqlOperation(queries.getContact, { id: originalInvoiceData.clientId })
      )

      let tax = parseFloat(getContact.taxRate)
      let taxAmount = (tax / 100) * parseFloat(creditInvoiceData.amount)
      let amountAfterTax = (parseFloat(creditInvoiceData.amount) + taxAmount).toFixed(2)
      let updateCompanyDetails = {
        id: companyDetails.id,
        _version: companyDetails._version,
        invoiceCount: newInvoiceNumber,
        invoicingYear: newInvoicingyear,
      }
      console.log('updateCompanyDetails: ', updateCompanyDetails)

      let newInvoice = {
        ...copyInvoiceData,
        id: nanoid(),
        tenantId: auth.userInfo.tenantId,
        activeFlag: true,
        title: `Credit Note ${newInvoiceNumber}`,
        invoiceNumber: `AV-${year}-${newInvoiceNumber}`,
        invoiceAmountPercentage: parseFloat(creditInvoiceData.invoiceAmountPercentage),
        amount: parseFloat(creditInvoiceData.amount),
        amountAfterTax: parseFloat(amountAfterTax),
        taxAmount: parseFloat(taxAmount),
        invoiceDate: dayjs().toISOString(),
        creditComments: creditInvoiceData.comments,
        originalInvoiceId: originalInvoiceData.id,
        invoiceType: 'credit',
      }
      console.log('newInvoice: ', newInvoice)

      let updateOriginalInvoice = {
        id: originalInvoiceData.id,
        _version: originalInvoiceData._version,
        creditInvoiceGenerated: true,
      }

      await API.graphql(graphqlOperation(mutations.createInvoices, { input: newInvoice }))
      await API.graphql(
        graphqlOperation(mutations.updateInvoices, { input: updateOriginalInvoice })
      )

      const updatedCompanyInfo = await API.graphql(
        graphqlOperation(mutations.updateCompany, { input: updateCompanyDetails })
      )
      await dispatch(fetchInvoices())
      makeSuccessToast(i18next.t('invoices:toasts.createdInvoice'))
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const createInvoice = createAsyncThunk(
  'invoices/createInvoice',
  async (
    { proposalData, formData, currentProposal, index },
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      const { auth, quotes } = getState()
      const {
        data: { getCompany },
      } = await API.graphql(graphqlOperation(queries.getCompany, { id: auth.userInfo.tenantId }))

      const {
        data: { getContact },
      } = await API.graphql(graphqlOperation(queries.getContact, { id: formData.contact }))
      const {
        data: { getProposals: updateDrafts },
      } = await API.graphql(graphqlOperation(queries.getProposals, { id: currentProposal.id }))

      delete updateDrafts._deleted
      delete updateDrafts._lastChangedAt
      delete updateDrafts.quote
      delete updateDrafts.invoices
      updateDrafts.clientId = formData.contact
      updateDrafts.clientName = getContact.name
      if (updateDrafts.status === 'Generate') {
        for (let i = 0; i < formData.drafts.length; i++) {
          formData.drafts[i].taxAmount =
            (parseFloat(getContact.taxRate) / 100) * parseFloat(formData.drafts[i].amount)
          formData.drafts[i].amountAfterTax =
            parseFloat(formData.drafts[i].taxAmount) + parseFloat(formData.drafts[i].amount)
          formData.drafts[i].invoiceDate = dayjs(formData.drafts[i].invoiceDate).toISOString()
          formData.drafts[i].invoiceDueDate = dayjs(formData.drafts[i].invoiceDueDate).toISOString()
          if (i === 0) {
            formData.drafts[i].balancePercentage =
              100 - parseFloat(formData.drafts[i].proposalAmountPercentage)
            formData.drafts[i].balanceAmount =
              parseFloat(updateDrafts.total) - parseFloat(formData.drafts[i].amount)
          } else {
            let totalAmount = 0
            let totalPercent = 0
            for (let j = 0; j <= i; j++) {
              totalAmount += parseFloat(formData.drafts[j].amount)
              totalPercent += parseFloat(formData.drafts[j].proposalAmountPercentage)
            }
            formData.drafts[i].balancePercentage = 100 - totalPercent
            formData.drafts[i].balanceAmount = parseFloat(updateDrafts.total) - totalAmount
          }
        }
      } else {
        for (let i = 0; i < formData.drafts.length; i++) {
          formData.drafts[i].invoiceId = updateDrafts.drafts[i].invoiceId
          formData.drafts[i].taxAmount =
            (parseFloat(getContact.taxRate) / 100) * parseFloat(formData.drafts[i].amount)
          formData.drafts[i].amountAfterTax =
            parseFloat(formData.drafts[i].taxAmount) + parseFloat(formData.drafts[i].amount)
          formData.drafts[i].generated =
            updateDrafts.drafts !== null ? updateDrafts.drafts[i].generated : false
          formData.drafts[i].invoiceDate = dayjs(formData.drafts[i].invoiceDate).toISOString()
          formData.drafts[i].invoiceDueDate = dayjs(formData.drafts[i].invoiceDueDate).toISOString()
          if (i === 0) {
            formData.drafts[i].balancePercentage =
              100 - parseFloat(formData.drafts[i].proposalAmountPercentage)
            formData.drafts[i].balanceAmount =
              parseFloat(updateDrafts.total) - parseFloat(formData.drafts[i].amount)
          } else {
            let totalAmount = 0
            let totalPercent = 0
            for (let j = 0; j <= i; j++) {
              totalAmount += parseFloat(formData.drafts[j].amount)
              totalPercent += parseFloat(formData.drafts[j].proposalAmountPercentage)
            }
            formData.drafts[i].balancePercentage = 100 - totalPercent
            formData.drafts[i].balanceAmount = parseFloat(updateDrafts.total) - totalAmount
          }
        }
      }

      updateDrafts.drafts = formData.drafts
      updateDrafts.noofInvoices = formData.invoices
      updateDrafts.paymentTerms = formData.paymentTerms
      // let clientName = `${getContact.firstName} ${getContact.lastName}`
      let clientName = `${formData.contactFirstName} ${formData.contactLastName}`
      // let clientName = proposalData.contactText
      let clientAddress = `${getContact.line01}${getContact.line01 && '-'}${getContact.line02}${
        getContact.line02 && '-'
      }${getContact.country}`
      updateDrafts.contactName = clientName
      updateDrafts.clientEmail = getContact.email
      updateDrafts.clientAddress = clientAddress
      updateDrafts.clientIdNumber = getContact.idnumber
      updateDrafts.clientVatNumber = getContact.vatNumber
      updateDrafts.clientCurrency = getContact.currency
      updateDrafts.clientTaxRate = getContact.taxRate
      updateDrafts.clientTaxType = getContact.taxType
      updateDrafts.clientNote = getContact.note
      updateDrafts.isDetailed = formData.isDetailed
      updateDrafts.bankProfileNumber = formData.bankAccount
      if (formData.bankAccount === 'bankProfile1') {
        updateDrafts.proposalsBankProfileName = getCompany.bankProfileName
        updateDrafts.proposalsBank = getCompany.bank
        updateDrafts.proposalsIban = getCompany.iban
        updateDrafts.proposalsBic = getCompany.bic
        updateDrafts.proposalsAccountName = getCompany.accountName
      } else if (formData.bankAccount === 'bankProfile2') {
        updateDrafts.proposalsBankProfileName = getCompany.bankProfileName2
        updateDrafts.proposalsBank = getCompany.bank2
        updateDrafts.proposalsIban = getCompany.iban2
        updateDrafts.proposalsBic = getCompany.bic2
        updateDrafts.proposalsAccountName = getCompany.accountName2
      } else if (formData.bankAccount === 'bankProfile3') {
        updateDrafts.proposalsBankProfileName = getCompany.bankProfileName3
        updateDrafts.proposalsBank = getCompany.bank3
        updateDrafts.proposalsIban = getCompany.iban3
        updateDrafts.proposalsBic = getCompany.bic3
        updateDrafts.proposalsAccountName = getCompany.accountName3
      }
      await API.graphql(graphqlOperation(mutations.updateProposals, { input: updateDrafts }))
      dispatch(fetchProposals())
      const {
        data: { getProposals },
      } = await API.graphql(graphqlOperation(queries.getProposals, { id: currentProposal.id }))

      if (getProposals.drafts[index]?.generated === true) {
        makeErrorToast(i18next.t('invoices:errorToasts.invExists'))
      } else {
        const zeroPad = (num, places) => String(num).padStart(places, '0')
        const {
          data: { getContact },
        } = await API.graphql(graphqlOperation(queries.getContact, { id: formData.contact }))
        const {
          data: { getCompany: companyDetails },
        } = await API.graphql(graphqlOperation(queries.getCompany, { id: auth.userInfo.tenantId }))
        let newInvoiceNumber = 0
        let newInvoicingyear = companyDetails.invoicingYear
        if (companyDetails.invoiceCount === null) {
          newInvoiceNumber = zeroPad(1, 4)
        } else {
          newInvoiceNumber = zeroPad(parseInt(companyDetails.invoiceCount) + 1, 4)
          newInvoicingyear = dayjs().toISOString()
        }
        if (companyDetails.invoicingYear === null) {
          newInvoicingyear = dayjs().toISOString()
        } else {
          let today = dayjs().year()
          let oldInvoicingYear = dayjs(companyDetails.invoicingYear).year()
          if (today > oldInvoicingYear) {
            newInvoicingyear = dayjs().toISOString()
            newInvoiceNumber = zeroPad(1, 4)
          }
        }
        delete companyDetails._deleted
        delete companyDetails._lastChangedAt
        delete companyDetails.createdAt
        delete companyDetails.updatedAt
        companyDetails.invoiceCount = newInvoiceNumber
        console.log('newInvoiceNumber: ', newInvoiceNumber)
        companyDetails.invoicingYear = newInvoicingyear
        console.log('companyDetails: ', companyDetails)
        let readyStatusArray = formData.drafts.map(d => d.readyStatus)
        if (readyStatusArray.every(r => r === true)) {
          getProposals.status = 'Generated'
        } else if (readyStatusArray.some(r => r === true)) {
          getProposals.status = 'Partially Generated'
        }
        delete getProposals._deleted
        delete getProposals._lastChangedAt
        delete getProposals.quote
        delete getProposals.invoices
        const newInvoiceId = nanoid()
        getProposals.drafts[index].generated = true
        getProposals.drafts[index].readyStatus = true
        getProposals.drafts[index].invoiceId = newInvoiceId
        getProposals.clientId = formData.contact
        getProposals.clientName = getContact.name

        let tax = parseFloat(getContact.taxRate)
        let taxAmount = (tax / 100) * parseFloat(proposalData.amount)
        let amountAfterTax = (parseFloat(proposalData.amount) + taxAmount).toFixed(2)
        let year = dayjs(newInvoicingyear).year()
        let newInvoice = {
          id: newInvoiceId,
          tenantId: auth.userInfo.tenantId,
          proposalId: currentProposal.id,
          activeFlag: true,
          clientId: formData.contact,
          clientName: getContact.name,
          contactName: getProposals.contactName,
          clientEmail: getProposals.clientEmail,
          clientAddress: getProposals.clientAddress,
          clientIdNumber: getProposals.clientIdNumber,
          clientVatNumber: getProposals.clientVatNumber,
          clientCurrency: getProposals.clientCurrency,
          clientTaxRate: getProposals.clientTaxRate,
          clientTaxType: getProposals.clientTaxType,
          clientNote: getProposals.clientNote,
          isDetailed: getProposals.isDetailed,
          bankProfileNumber: getProposals.bankProfileNumber,
          invoicesBankProfileName: getProposals.proposalsBankProfileName,
          invoicesBank: getProposals.proposalsBank,
          invoicesIban: getProposals.proposalsIban,
          invoicesBic: getProposals.proposalsBic,
          invoicesAccountName: getProposals.proposalsAccountName,
          title: `Invoice ${newInvoiceNumber}`,
          invoiceNumber: `INV-${year}-${newInvoiceNumber}`,
          poNumber: proposalData.poNumber,
          quoteName: getProposals.quoteName,
          projectName: getProposals.projectName,
          invoiceAmountPercentage: proposalData.proposalAmountPercentage,
          amount: parseFloat(proposalData.amount),
          amountAfterTax: parseFloat(amountAfterTax),
          taxAmount: parseFloat(getProposals.drafts[index].taxAmount),
          balancePercentage: parseFloat(getProposals.drafts[index].balancePercentage),
          balanceAmount: parseFloat(getProposals.drafts[index].balanceAmount),
          invoiceDate: proposalData.invoiceDate,
          invoiceDueDate: proposalData.invoiceDueDate,
          description: proposalData.description,
          paymentStatus: 'Pending',
          proposalsDraft: getProposals.drafts,
          invoiceType: 'original',
          creditInvoiceGenerated: false,
        }

        // getProposals.drafts[index].invoiceId = newInvoice.id
        let updateContact = {}
        console.log('proposalData: ', proposalData)
        if (!isEqual(getContact.firstName, formData.contactFirstName)) {
          updateContact.firstName = formData.contactFirstName
        }
        if (!isEqual(getContact.lastName, formData.contactLastName)) {
          updateContact.lastName = formData.contactLastName
        }
        console.log('updateContact: ', updateContact)
        await API.graphql(graphqlOperation(mutations.createInvoices, { input: newInvoice }))
        const newCurrentProposal = await API.graphql(
          graphqlOperation(mutations.updateProposals, { input: getProposals })
        )
        const updatedCompanyInfo = await API.graphql(
          graphqlOperation(mutations.updateCompany, { input: companyDetails })
        )
        dispatch(setCurrentProposal(newCurrentProposal.data.updateProposals))
        if (Object.keys(updateContact).length > 0) {
          updateContact.id = getContact.id
          updateContact._version = getContact._version
          await API.graphql(graphqlOperation(mutations.updateContact, { input: updateContact }))
        }
        await dispatch(fetchInvoices())
        await dispatch(fetchProposals())
        await dispatch(fetchContacts())
        makeSuccessToast(i18next.t('invoices:toasts.createdInvoice'))
      }
      const {
        data: {
          invoicesByProposalId: { items },
        },
      } = await API.graphql(
        graphqlOperation(queries.invoicesByProposalId, {
          proposalId: getProposals.id,
          filter: { activeFlag: { eq: true } },
        })
      )
      let sortedInvoices = orderBy(items, ['invoiceNumber'], ['asc'])
      await dispatch(setCurrentProposalInvoices(sortedInvoices))
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)
export const updateInvoice = createAsyncThunk(
  'invoices/updateInvoice',
  async ({ proposalData, formData, currentProposal, index }, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { getInvoices },
      } = await API.graphql(
        graphqlOperation(queries.getInvoices, { id: currentProposal.drafts[index].invoiceId })
      )
      const {
        data: { getProposals },
      } = await API.graphql(graphqlOperation(queries.getProposals, { id: currentProposal.id }))
      let updatedInvoice = {
        id: getInvoices.id,
        _version: getInvoices._version,
      }
      let originalDrafts = getInvoices
      let copyProposalData = proposalData
      delete copyProposalData.amount
      delete copyProposalData.proposalAmountPercentage
      delete copyProposalData.readyStatus

      for (const property in copyProposalData) {
        if (!isEqual(copyProposalData[property], originalDrafts[property])) {
          updatedInvoice[property] = copyProposalData[property]
          if (
            property === 'invoiceDueDate' &&
            getInvoices.paymentStatus !== 'Paid' &&
            getInvoices.paymentStatus !== 'Archived'
          ) {
            if (dayjs(copyProposalData.invoiceDueDate).diff(dayjs(), 'day') >= 0) {
              updatedInvoice.paymentStatus = 'Pending'
            } else if (dayjs(copyProposalData.invoiceDueDate).diff(dayjs(), 'day') < 0) {
              updatedInvoice.paymentStatus = 'Overdue'
            }
          }
        }
      }

      for (let i = 0; i < parseInt(formData['invoices']); i++) {
        formData.drafts[i].invoiceId = getProposals.drafts[i].invoiceId
      }
      updatedInvoice.proposalsDraft = formData.drafts
      await API.graphql(graphqlOperation(mutations.updateInvoices, { input: updatedInvoice }))
      dispatch(fetchInvoices())
      makeSuccessToast(i18next.t('invoices:toasts.updatedInvoice'))
      const {
        data: {
          invoicesByProposalId: { items },
        },
      } = await API.graphql(
        graphqlOperation(queries.invoicesByProposalId, {
          proposalId: getProposals.id,
          filter: { activeFlag: { eq: true } },
        })
      )
      let sortedInvoices = orderBy(items, ['invoiceNumber'], ['asc'])
      await dispatch(setCurrentProposalInvoices(sortedInvoices))
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const updatePaymentStatus = createAsyncThunk(
  'invoices/updatePaymentStatus',
  async ({ date, currentInvoice }, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { getInvoices },
      } = await API.graphql(graphqlOperation(queries.getInvoices, { id: currentInvoice }))
      delete getInvoices._deleted
      delete getInvoices._lastChangedAt
      delete getInvoices.contact
      delete getInvoices.proposals
      getInvoices.paymentStatus = 'Paid'
      getInvoices.paidDate = dayjs(date).toISOString()
      await API.graphql(graphqlOperation(mutations.updateInvoices, { input: getInvoices }))
      dispatch(fetchInvoices())
      makeSuccessToast(i18next.t('invoices:toasts.markPaid'))
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)
export const markAsUnpaid = createAsyncThunk(
  'invoices/markAsUnpaid',
  async ({ currentInvoice }, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { getInvoices },
      } = await API.graphql(graphqlOperation(queries.getInvoices, { id: currentInvoice }))
      delete getInvoices._deleted
      delete getInvoices._lastChangedAt
      delete getInvoices.contact
      delete getInvoices.proposals
      getInvoices.paymentStatus = 'Paid'

      if (dayjs(getInvoices.invoiceDueDate).diff(dayjs(), 'day') >= 0) {
        getInvoices.paymentStatus = 'Pending'
      } else if (dayjs(getInvoices.invoiceDueDate).diff(dayjs(), 'day') < 0) {
        getInvoices.paymentStatus = 'Overdue'
      }
      await API.graphql(graphqlOperation(mutations.updateInvoices, { input: getInvoices }))
      dispatch(fetchInvoices())
      makeSuccessToast(i18next.t('invoices:toasts.markUnpaid'))
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)
export const dailyUpdatePaymentStatus = createAsyncThunk(
  'invoices/dailyUpdatePaymentStatus',
  async ({ currentInvoice }, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { getInvoices },
      } = await API.graphql(graphqlOperation(queries.getInvoices, { id: currentInvoice }))

      let updatedInvoices = {
        id: getInvoices.id,
        _version: getInvoices._version,
        paymentStatus: 'Overdue',
      }
      await API.graphql(graphqlOperation(mutations.updateInvoices, { input: updatedInvoices }))
      dispatch(fetchInvoices())
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)
export const updateArchiveStatus = createAsyncThunk(
  'invoices/updateArchiveStatus',
  async ({ currentInvoice }, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { getInvoices },
      } = await API.graphql(graphqlOperation(queries.getInvoices, { id: currentInvoice }))
      delete getInvoices._deleted
      delete getInvoices._lastChangedAt
      delete getInvoices.contact
      delete getInvoices.proposals
      let dueStatus = ''
      if (dayjs(getInvoices.invoiceDueDate).diff(dayjs(), 'day') >= 0) {
        dueStatus = 'Pending'
      } else if (dayjs(getInvoices.invoiceDueDate).diff(dayjs(), 'day') < 0) {
        dueStatus = 'Overdue'
      }
      getInvoices.paymentStatus = getInvoices.paymentStatus !== 'Archived' ? 'Archived' : dueStatus
      getInvoices.archivedDate = dayjs().toISOString()
      await API.graphql(graphqlOperation(mutations.updateInvoices, { input: getInvoices }))
      dispatch(fetchInvoices())
      makeSuccessToast(
        `Invoice succesfully  ${
          getInvoices.paymentStatus === 'Archived' ? 'archived' : 'unarchived'
        }`
      )
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)
export const viewPdf = createAsyncThunk(
  'invoices/viewPdf',
  async ({ row }, { dispatch, rejectWithValue, getState }) => {
    try {
      console.log('row: ', row)
      const { invoices } = getState()
      let originalInvoiceData = invoices.invoicesData.filter(
        invData => invData.id === row.originalInvoiceId
      )[0]
      dispatch(setCurrentInvoice(row))
      dispatch(setCurrentProposal(row.proposals))
      dispatch(setOriginalInvoiceData(originalInvoiceData))
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const invoicesSlice = createSlice({
  name: 'invoices',
  initialState: {
    invoicesData: [],
    viewPdfStatus: 'idle',
    fetchInvoicesApiStatus: 'idle',
    createInvoiceApiStatus: 'idle',
    updateInvoiceApiStatus: 'idle',
    updatePaymentApiStatus: 'idle',
    updateArchiveApiStatus: 'idle',
    viewPdfModalOpenStatus: false,
    creditInvoiceModalOpenStatus: false,
    invoicePaidConfirmationDialogOpenStatus: false,
    invoiceUnpaidConfirmationDialogOpenStatus: false,
    invoiceArchivedConfirmationDialogOpenStatus: false,
    invoiceDownloadConfirmationDialogOpenStatus: false,
    originalInvoiceData: null,
    currentInvoice: null,
    error: null,
  },
  reducers: {
    setInvoiceArchivedConfirmationDialogOpenStatus: (state, action) => {
      state.invoiceArchivedConfirmationDialogOpenStatus = action.payload.status
    },
    setInvoicePaidConfirmationDialogOpenStatus: (state, action) => {
      state.invoicePaidConfirmationDialogOpenStatus = action.payload.status
    },
    setInvoiceUnpaidConfirmationDialogOpenStatus: (state, action) => {
      state.invoiceUnpaidConfirmationDialogOpenStatus = action.payload.status
    },
    setInvoiceDownloadConfirmationDialogOpenStatus: (state, action) => {
      state.invoiceDownloadConfirmationDialogOpenStatus = action.payload.status
    },
    setCreditInvoiceModalOpenStatus: (state, action) => {
      state.creditInvoiceModalOpenStatus = action.payload
    },
    setFetchInvoicesApiStatus: (state, action) => {
      state.fetchInvoicesApiStatus = 'loading'
    },
    setCurrentInvoice: (state, action) => {
      state.currentInvoice = action.payload
    },
    setOriginalInvoiceData: (state, action) => {
      state.originalInvoiceData = action.payload
    },
    setViewPdfModalOpenStatus: (state, action) => {
      state.viewPdfModalOpenStatus = action.payload
    },
  },
  extraReducers: {
    [fetchInvoices.pending]: (state, action) => {
      state.fetchInvoicesApiStatus = 'loading'
    },
    [fetchInvoices.fulfilled]: (state, action) => {
      state.fetchInvoicesApiStatus = 'succeeded'
      state.invoicesData = action.payload
    },
    [fetchInvoices.rejected]: (state, action) => {
      state.fetchInvoicesApiStatus = 'failed'
      state.error = action.error.message
    },
    [createInvoice.pending]: (state, action) => {
      state.createInvoiceApiStatus = 'loading'
    },
    [createInvoice.fulfilled]: (state, action) => {
      state.createInvoiceApiStatus = 'succeeded'
      state.invoiceDownloadConfirmationDialogOpenStatus = true
    },
    [createInvoice.rejected]: (state, action) => {
      state.createInvoiceApiStatus = 'failed'
      state.invoiceDownloadConfirmationDialogOpenStatus = false
      state.error = action.error.message
    },
    [createCreditInvoice.pending]: (state, action) => {
      state.createInvoiceApiStatus = 'loading'
    },
    [createCreditInvoice.fulfilled]: (state, action) => {
      state.createInvoiceApiStatus = 'succeeded'
      state.creditInvoiceModalOpenStatus = false
    },
    [createCreditInvoice.rejected]: (state, action) => {
      state.createInvoiceApiStatus = 'failed'
      state.error = action.error.message
    },
    [updateInvoice.pending]: (state, action) => {
      state.updateInvoiceApiStatus = 'loading'
    },
    [updateInvoice.fulfilled]: (state, action) => {
      state.updateInvoiceApiStatus = 'succeeded'
      state.invoiceDownloadConfirmationDialogOpenStatus = true
    },
    [updateInvoice.rejected]: (state, action) => {
      state.updateInvoiceApiStatus = 'failed'
      state.invoiceDownloadConfirmationDialogOpenStatus = false
      state.error = action.error.message
    },
    [updatePaymentStatus.pending]: (state, action) => {
      state.updatePaymentApiStatus = 'loading'
    },
    [updatePaymentStatus.fulfilled]: (state, action) => {
      state.updatePaymentApiStatus = 'succeeded'
      state.invoicePaidConfirmationDialogOpenStatus = false
    },
    [updatePaymentStatus.rejected]: (state, action) => {
      state.updatePaymentApiStatus = 'failed'
      state.invoicePaidConfirmationDialogOpenStatus = false
      state.error = action.error.message
    },
    [markAsUnpaid.pending]: (state, action) => {
      state.updatePaymentApiStatus = 'loading'
    },
    [markAsUnpaid.fulfilled]: (state, action) => {
      state.updatePaymentApiStatus = 'succeeded'
      state.invoiceUnpaidConfirmationDialogOpenStatus = false
    },
    [markAsUnpaid.rejected]: (state, action) => {
      state.updatePaymentApiStatus = 'failed'
      state.invoiceUnpaidConfirmationDialogOpenStatus = false
      state.error = action.error.message
    },
    [updateArchiveStatus.pending]: (state, action) => {
      state.updateArchiveApiStatus = 'loading'
    },
    [updateArchiveStatus.fulfilled]: (state, action) => {
      state.updateArchiveApiStatus = 'succeeded'
      state.invoiceArchivedConfirmationDialogOpenStatus = false
    },
    [updateArchiveStatus.rejected]: (state, action) => {
      state.updateArchiveApiStatus = 'failed'
      state.invoiceArchivedConfirmationDialogOpenStatus = false
      state.error = action.error.message
    },
    [viewPdf.pending]: (state, action) => {
      state.viewPdfStatus = 'loading'
    },
    [viewPdf.fulfilled]: (state, action) => {
      state.viewPdfStatus = 'succeeded'
      state.viewPdfModalOpenStatus = true
    },
    [viewPdf.rejected]: (state, action) => {
      state.viewPdfStatus = 'failed'
      state.viewPdfModalOpenStatus = false
      state.error = action.error.message
    },
  },
})

export const {
  setInvoiceArchivedConfirmationDialogOpenStatus,
  setInvoicePaidConfirmationDialogOpenStatus,
  setFetchInvoicesApiStatus,
  setInvoiceUnpaidConfirmationDialogOpenStatus,
  setInvoiceDownloadConfirmationDialogOpenStatus,
  setCreditInvoiceModalOpenStatus,
  setCurrentInvoice,
  setOriginalInvoiceData,
  setViewPdfModalOpenStatus,
} = invoicesSlice.actions

export default invoicesSlice.reducer
